import type { StandardDivProps } from '@sprinx/react-mui-components';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import clsx from 'clsx';
import { Link as RouterLink, useRouteMatch } from 'react-router-dom';
import {
  catalogueTaxonomiesHierarchySelector,
  ProductTaxonomyTypeHierarchyNode,
} from '../../../api/products/taxonomyTypes';
// import { localeState } from '../../../api/appState';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import { catalogueGlobalFilterState, getPathWithoutLocale } from '../../../api/products/catalogue';
import { catalogueTaxonomiesHierarchyLoyaltyProgramSelector } from '../../../api/products/taxonomyTypesLoyaltyProgram';

export type CataloguePageSubCategoriesProps = StandardDivProps<CataloguePageSubCategoriesClassKey>;

export type CataloguePageSubCategoriesClassKey = 'root' | 'subCategory' | 'subCategoryLabel';

const themeSettings = { name: 'CataloguePageSubCategories' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<CataloguePageSubCategoriesClassKey, {}>({
      root: {
        display: 'grid',
        columnGap: theme.spacing(2),
        rowGap: theme.spacing(2),
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        [theme.breakpoints.up('md')]: {
          gridTemplateColumns: 'repeat(auto-fill, 260px)',
        },
        gap: theme.spacing(2),
        padding: theme.spacing(2, 0),
        fontSize: '1rem',
      },
      subCategory: {
        display: 'grid',
        alignItems: 'center',
        position: 'relative',
        padding: theme.spacing(1, 6, 1, 1),
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[1],
        cursor: 'pointer',
        textDecoration: 'none',
        fontSize: '1rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
        minHeight: theme.spacing(9),
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          textDecoration: 'underline',
          '&:after': {
            right: theme.spacing(1), // posunout doprava při hoveru
          },
        },
        '&:after': {
          content: '">"',
          transform: 'scaleY(2) scaleX(0.5)',
          position: 'absolute',
          right: theme.spacing(2),
          color: theme.palette.text.primary,
          transitionDuration: '0.3s',
        },
      },
      subCategoryLabel: {},
    }),
  themeSettings,
);

function getCurrentTaxonomyFromUrl(
  taxonomies: ProductTaxonomyTypeHierarchyNode,
  url: string,
): ProductTaxonomyTypeHierarchyNode | null {
  const taxonomyAccordingToUrl = (node: ProductTaxonomyTypeHierarchyNode): ProductTaxonomyTypeHierarchyNode | null => {
    const pathWithoutLocale = getPathWithoutLocale(url);
    
    if (node.code === pathWithoutLocale) return node;

    for (const child of node.children || []) {
      const found = taxonomyAccordingToUrl(child);
      if (found) return found;
    }

    return null;
  };

  return taxonomyAccordingToUrl(taxonomies);
}

export function CataloguePageSubCategoriesLoyaltyProgram({
  className,
  classes: pClasses,
}: CataloguePageSubCategoriesProps): React.ReactElement | null {
  const classes = useStyles({ classes: pClasses });
  const { url } = useRouteMatch();
  const t = useTranslateWithFallback();
  // const locale = useRecoilValue(localeState);
  const taxonomies = useRecoilValue(catalogueTaxonomiesHierarchyLoyaltyProgramSelector);
  let urlCustom = url;
  if (
    urlCustom.startsWith('/vernostni-program') ||
    urlCustom.startsWith('/vernostny-program') ||
    urlCustom.startsWith('/loyalty-program')
  ) {
    const splitted = urlCustom.split('/');
    splitted[1] = 'loyaltyProgram';
    urlCustom = splitted.join('/');
  }
  const currentTaxonomy = getCurrentTaxonomyFromUrl(taxonomies, urlCustom);
  const setFilter = useSetRecoilState(catalogueGlobalFilterState);

  const handleClick: () => void = () => {
    setFilter({ searchText: undefined });
  };

  if (!currentTaxonomy || !currentTaxonomy.children || currentTaxonomy.children.length === 0) {
    return null;
  }

  return (
    <div className={clsx(classes.root, className)}>
      {currentTaxonomy.children.map((child) => (
        <RouterLink
          key={child.code}
          // to={`/${locale}${child.code}`}
          to={child.code.replace('/loyaltyProgram', `/${url.split('/')[1]}`)}
          className={classes.subCategory}
          onClick={handleClick}
        >
          <span className={classes.subCategoryLabel}>{t(child.label)}</span>
        </RouterLink>
      ))}
    </div>
  );
}

export default function CataloguePageSubCategories({
  className,
  classes: pClasses,
}: CataloguePageSubCategoriesProps): React.ReactElement | null {
  const classes = useStyles({ classes: pClasses });
  const { url } = useRouteMatch();
  const t = useTranslateWithFallback();
  // const locale = useRecoilValue(localeState);
  const taxonomies = useRecoilValue(catalogueTaxonomiesHierarchySelector);
  const currentTaxonomy = getCurrentTaxonomyFromUrl(taxonomies, url);
  const setFilter = useSetRecoilState(catalogueGlobalFilterState);

  const handleClick: () => void = () => {
    setFilter({ searchText: undefined });
  };

  if (!currentTaxonomy || !currentTaxonomy.children || currentTaxonomy.children.length === 0) {
    return null;
  }

  return (
    <div className={clsx(classes.root, className)}>
      {currentTaxonomy.children.map((child) => (
        <RouterLink
          key={child.code}
          // to={`/${locale}${child.code}`}
          to={child.code}
          className={classes.subCategory}
          onClick={handleClick}
        >
          <span className={classes.subCategoryLabel}>{t(child.label)}</span>
        </RouterLink>
      ))}
    </div>
  );
}
